import type { Provider } from "./auth";

const ORG_KEY = "lastOrg";

export function emailDomain(email: string): string | null {
  const trimmed = email.trim();
  const at = trimmed.lastIndexOf("@");
  if (at < 1) return null;
  const domain = trimmed.slice(at + 1).toLowerCase();
  return /^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(domain) ? domain : null;
}

export function lastOrg(): string | null {
  try {
    return localStorage.getItem(ORG_KEY);
  } catch {
    return null;
  }
}

export function rememberOrg(slug: string): void {
  try {
    localStorage.setItem(ORG_KEY, slug);
  } catch {
    // Without storage, the user types the email address again on the next visit.
  }
}

export function forgetOrg(): void {
  try {
    localStorage.removeItem(ORG_KEY);
  } catch {
    // Nothing to clean.
  }
}

export function linkCode(hash: string = location.hash): string | null {
  return new URLSearchParams(hash.replace(/^#/, "")).get("link");
}

export function linkConnect(hash: string = location.hash): Provider | null {
  const name = new URLSearchParams(hash.replace(/^#/, "")).get("connect");
  return name === "google" || name === "microsoft" ? name : null;
}

export const MAX_EMAIL = 254;
export const LINK_TIMEOUT_MS = 20_000;
export const NOT_VALID =
  "This sign-in link is not valid. It was used, or it is more than 15 minutes old. Enter your email address to get a new link.";
export const NOT_SIGNED_IN = "We could not sign you in. Enter your email address to get a new link.";
const NO_CONNECTION = "Cannot reach Xflow. Check your connection, then try again.";

const status = (error: unknown) =>
  typeof error === "object" && error !== null && "status" in error ? Number(error.status) : null;
export const errorCode = (error: unknown) =>
  typeof error === "object" && error !== null && "code" in error ? String(error.code) : "";

export function sentText(address: string): string {
  return `If ${address} has an account, we sent a sign-in link to it. Open the link in the email within 15 minutes. If no email comes in 5 minutes, look in your spam folder.`;
}

export function startSentText(address: string): string {
  return `If ${address} has an account in an organization that uses email sign-in links, we sent a sign-in link to it. Open the link in the email within 15 minutes. If no email comes, ask your administrator for the link to your organization. Or open your organization by its short name and use the sign-in buttons there.`;
}

/** Give the link error words for an organization, which has email links only when withEmail is true. */
export function linkErrorText(error: string, withEmail: boolean, labels: string[]): string {
  if (!error || withEmail) return error;
  const first = error === NOT_VALID ? "This sign-in link is not valid." : "We could not sign you in.";
  if (labels.length === 0) return `${first} Ask your administrator how to sign in.`;
  return `${first} Sign in with ${labels.join(" or ")}.`;
}

export function sendError(error: unknown): string {
  const answered = status(error);
  if (answered === 422) return "Check the email address, then try again.";
  if (answered !== null) return "The server has a problem. Try again in a few minutes.";
  return NO_CONNECTION;
}

export function redeemError(error: unknown): string {
  const answered = status(error);
  return answered === 400 || answered === 422 ? NOT_VALID : NOT_SIGNED_IN;
}

const KNOWN_PROVIDER_ERRORS = new Set([
  "auth/popup-closed-by-user",
  "auth/cancelled-popup-request",
  "auth/popup-blocked",
  "auth/network-request-failed",
  "auth/admin-restricted-operation",
  "auth/user-not-found",
]);

export function providerDetail(error: unknown): string {
  const reason = errorCode(error);
  return KNOWN_PROVIDER_ERRORS.has(reason) ? "" : reason || String(error);
}

const GOOGLE_MAIL = new Set(["gmail.com", "googlemail.com"]);
const MICROSOFT_MAIL = new Set(["outlook.com", "hotmail.com", "live.com", "msn.com"]);

export function primaryFor(email: string | null): "google" | "microsoft" | "email" {
  const domain = email ? emailDomain(email) : null;
  if (domain && GOOGLE_MAIL.has(domain)) return "google";
  if (domain && MICROSOFT_MAIL.has(domain)) return "microsoft";
  return "email";
}

export function providerError(error: unknown, label: string): string {
  const reason = errorCode(error);
  if (reason === "auth/popup-closed-by-user" || reason === "auth/cancelled-popup-request") return "";
  if (reason === "auth/popup-blocked") {
    return `Your browser blocked the ${label} window. Allow pop-ups for this site, then try again.`;
  }
  if (reason === "auth/network-request-failed") return NO_CONNECTION;
  if (reason === "auth/admin-restricted-operation" || reason === "auth/user-not-found") {
    return `This ${label} account has no access here. Ask your administrator for an invite.`;
  }
  return `${label} sign-in did not work. Try again.`;
}

export const redirectLostText = (label: string) =>
  `${label} sign-in did not finish. Try again. If it stops again, sign in with your email address.`;

export function connectText(address: string, label: string): string {
  return `You already have an account for ${address}. To add ${label} sign-in to it, open the link that we sent to ${address}. Open it in this browser within 15 minutes. You do this one time only.`;
}

export const connectedText = (label: string) => `${label} sign-in is added. Next time, press Sign in with ${label}.`;

export const connectFailedText = (label: string) =>
  `You are signed in, but we could not add ${label} sign-in. To try again, open the avatar menu at the top right, press Sign out, then press Sign in with ${label}.`;

export const connectMissedText = (label: string) =>
  `You are signed in, but ${label} sign-in is not added yet. To add it, open the avatar menu at the top right, press Sign out, then press Sign in with ${label}. Then open the new link in the same browser.`;

export function tokenUid(token: string): string | null {
  try {
    const payload = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const uid: unknown = JSON.parse(atob(payload)).uid;
    return typeof uid === "string" ? uid : null;
  } catch {
    return null;
  }
}

export function withTimeout<T>(work: Promise<T>, ms: number = LINK_TIMEOUT_MS): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error("timeout")), ms);
    work.then(resolve, reject).finally(() => clearTimeout(timer));
  });
}
